/* ============================================================
 * REPAD MOTION
 * Lightweight scroll reveals, count-ups and sticky-state hooks.
 * Respects prefers-reduced-motion. No external dependencies.
 * ============================================================ */
(function () {
  'use strict';

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var supportsIO = 'IntersectionObserver' in window;

  /* ---- Scroll reveal ----
     Picks up two conventions:
       1) [data-reveal] attribute  → adds .is-revealed when in view
       2) .repad-reveal / .repad-reveal-up / .repad-reveal-scale → adds .is-visible
     Stagger: any parent with [data-reveal-stagger] or .repad-stagger gets its direct
     reveal children auto-assigned --repad-reveal-delay (default 100ms step, configurable
     via [data-reveal-stagger="80"] or style="--repad-stagger-step: 80ms"). */

  function applyStagger() {
    var stepDefault = 100;
    var parents = document.querySelectorAll('[data-reveal-stagger], .repad-stagger');
    parents.forEach(function (parent) {
      var step = parseInt(parent.getAttribute('data-reveal-stagger'), 10);
      if (isNaN(step) || step <= 0) step = stepDefault;
      /* Optional base offset on the parent so the stagger sequence can start AFTER
         earlier in-section reveals. E.g. parent data-reveal-delay="340" + step 60
         → children get 340ms, 400ms, 460ms… */
      var base = parseInt(parent.getAttribute('data-reveal-delay'), 10);
      if (isNaN(base) || base < 0) base = 0;
      var children = parent.querySelectorAll(':scope > [data-reveal], :scope > .repad-reveal, :scope > .repad-reveal-up, :scope > .repad-reveal-scale');
      children.forEach(function (child, i) {
        /* Don't overwrite an explicit per-element delay if author set one inline */
        if (!child.style.getPropertyValue('--repad-reveal-delay')) {
          child.style.setProperty('--repad-reveal-delay', (base + i * step) + 'ms');
        }
      });
    });

    /* Convenience: [data-reveal-delay="120"] → --repad-reveal-delay: 120ms
       Skips elements that are themselves stagger parents (their attr is the base
       offset for their children, not for the parent itself, which usually isn't
       a reveal target). */
    document.querySelectorAll('[data-reveal-delay]').forEach(function (el) {
      if (el.hasAttribute('data-reveal-stagger') || el.classList.contains('repad-stagger')) return;
      var v = parseInt(el.getAttribute('data-reveal-delay'), 10);
      if (!isNaN(v) && !el.style.getPropertyValue('--repad-reveal-delay')) {
        el.style.setProperty('--repad-reveal-delay', v + 'ms');
      }
    });
  }

  function initReveals() {
    applyStagger();

    var attrEls = document.querySelectorAll('[data-reveal]');
    var classEls = document.querySelectorAll('.repad-reveal, .repad-reveal-up, .repad-reveal-scale');
    if (!attrEls.length && !classEls.length) return;

    if (prefersReducedMotion || !supportsIO) {
      attrEls.forEach(function (el) { el.classList.add('is-revealed'); });
      classEls.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            if (entry.target.hasAttribute('data-reveal')) {
              entry.target.classList.add('is-revealed');
            } else {
              entry.target.classList.add('is-visible');
            }
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    attrEls.forEach(function (el) { observer.observe(el); });
    classEls.forEach(function (el) { observer.observe(el); });
  }

  /* ---- Count-up ---- */
  function countUp(el) {
    var target = parseFloat(el.getAttribute('data-countup'));
    if (isNaN(target)) return;

    var prefix = el.getAttribute('data-countup-prefix') || '';
    var suffix = el.getAttribute('data-countup-suffix') || '';
    var duration = parseInt(el.getAttribute('data-countup-duration') || '1200', 10);
    var decimals = parseInt(el.getAttribute('data-countup-decimals') || '0', 10);
    var startTime = null;

    function format(v) {
      return prefix + v.toLocaleString('nl-NL', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      }) + suffix;
    }

    function frame(ts) {
      if (!startTime) startTime = ts;
      var progress = Math.min((ts - startTime) / duration, 1);
      var eased = 1 - Math.pow(1 - progress, 3);
      el.textContent = format(target * eased);
      if (progress < 1) requestAnimationFrame(frame);
      else el.textContent = format(target);
    }

    requestAnimationFrame(frame);
  }

  function initCountUps() {
    var counters = document.querySelectorAll('[data-countup]');
    if (!counters.length) return;

    if (prefersReducedMotion || !supportsIO) {
      counters.forEach(function (el) {
        var v = parseFloat(el.getAttribute('data-countup'));
        if (!isNaN(v)) {
          var prefix = el.getAttribute('data-countup-prefix') || '';
          var suffix = el.getAttribute('data-countup-suffix') || '';
          var decimals = parseInt(el.getAttribute('data-countup-decimals') || '0', 10);
          el.textContent = prefix + v.toLocaleString('nl-NL', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
          }) + suffix;
        }
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            countUp(entry.target);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.3 }
    );

    counters.forEach(function (el) { observer.observe(el); });
  }

  /* ---- Sticky header / sticky-bar scrolled-state ---- */
  function initStickyState() {
    var els = document.querySelectorAll('[data-repad-sticky]');
    if (!els.length) return;

    var ticking = false;
    function update() {
      var y = window.scrollY || window.pageYOffset;
      var threshold = 24;
      els.forEach(function (el) {
        var custom = parseInt(el.getAttribute('data-repad-sticky-threshold') || '', 10);
        var t = isNaN(custom) ? threshold : custom;
        if (y > t) el.setAttribute('data-scrolled', 'true');
        else el.removeAttribute('data-scrolled');
      });
      ticking = false;
    }

    function onScroll() {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    }

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---- Product parallax ----
     Elements with [data-repad-parallax] drift slightly against
     scroll (translateY based on distance from viewport center).
     Subtle by design: max travel ~24px, factor configurable via
     data-repad-parallax="0.08". Desktop only (>=881px), disabled
     for prefers-reduced-motion. Apply to plain <img> elements, NOT
     to [data-reveal] containers, so reveal transitions don't fight
     the parallax transform.
     NOTE: deliberately namespaced; Impulse's own sections use
     [data-parallax] for their theme.js parallax and must not be
     touched by this module. */
  function initParallax() {
    if (prefersReducedMotion) return;
    var els = document.querySelectorAll('[data-repad-parallax]');
    if (!els.length) return;

    var items = [];
    els.forEach(function (el) {
      var factor = parseFloat(el.getAttribute('data-repad-parallax'));
      if (isNaN(factor) || factor <= 0) factor = 0.06;
      items.push({ el: el, factor: Math.min(factor, 0.2) });
    });

    var active = window.innerWidth >= 881;
    var ticking = false;

    function update() {
      ticking = false;
      if (!active) return;
      var vh = window.innerHeight;
      items.forEach(function (item) {
        var rect = item.el.getBoundingClientRect();
        if (rect.bottom < -100 || rect.top > vh + 100) return;
        var center = rect.top + rect.height / 2;
        var offset = (center - vh / 2) * -item.factor;
        if (offset > 24) offset = 24;
        if (offset < -24) offset = -24;
        item.el.style.transform = 'translate3d(0,' + offset.toFixed(1) + 'px,0)';
      });
    }

    function onScroll() {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    }

    function onResize() {
      var nowActive = window.innerWidth >= 881;
      if (nowActive !== active) {
        active = nowActive;
        if (!active) {
          items.forEach(function (item) { item.el.style.transform = ''; });
        }
      }
      onScroll();
    }

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
  }

  /* ---- FAQ accordion (smooth height for native details) ---- */
  function initAccordions() {
    document.querySelectorAll('[data-repad-accordion] details').forEach(function (d) {
      var summary = d.querySelector('summary');
      var content = d.querySelector('.repad-accordion__content');
      if (!summary || !content) return;
      d.addEventListener('toggle', function () {
        if (prefersReducedMotion) return;
        if (d.open) {
          content.style.maxHeight = content.scrollHeight + 'px';
        } else {
          content.style.maxHeight = '0px';
        }
      });
    });
  }

  /* ---- Init ---- */
  function init() {
    initReveals();
    initCountUps();
    initStickyState();
    initAccordions();
    initParallax();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
